import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { NAV } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";
import { useStores } from "@/context/StoreContext";
import { cn } from "@/lib/utils";
import { Menu, LogOut, ChevronsUpDown } from "lucide-react";
import StockAlerts from "@/components/StockAlerts";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function AppLayout({ children }) {
  const { user, logout, can } = useAuth();
  const { stores, activeStore, setActiveStore } = useStores();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const Sidebar = (
    <aside className="flex h-full w-64 flex-col bg-slate-900 text-slate-300">
      <div className="flex items-center px-5 h-16 border-b border-slate-800">
        <img src="/logo-ascendra.png" alt="SPH Ascendra" className="h-10 w-auto max-w-[190px] object-contain" data-testid="sidebar-logo" />
      </div>
      <nav className="sidebar-scroll flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {NAV.map((group, gi) => {
          const items = group.items.filter((it) => can(it.perm));
          if (items.length === 0) return null;
          return (
            <div key={gi}>
              {group.section && (
                <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                  {group.section}
                </p>
              )}
              <div className="space-y-0.5">
                {items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <NavLink
                      key={it.path}
                      to={it.path}
                      end={it.path === "/"}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`nav-${it.path.replace(/^\//, "").replace(/\//g, "-") || "dashboard"}`}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                          isActive
                            ? "bg-indigo-600 text-white font-medium shadow-sm"
                            : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60"
                        )
                      }
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{it.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>
      <div className="border-t border-slate-800 p-3">
        <div className="flex items-center gap-3 rounded-lg px-2 py-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-500/20 text-indigo-300 text-xs font-bold">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">{user?.name}</p>
            <p className="truncate text-[11px] text-slate-500">{user?.role_name}</p>
          </div>
        </div>
      </div>
    </aside>
  );

  // Store filter ONLY applies to store-related operational data.
  // Global product/inventory pages are store-independent, so hide the selector there.
  const STORE_AWARE_PATHS = ["/", "/orders", "/advertising", "/complaints", "/sales-analytics", "/inventory/retur"];
  const isStoreAwarePage = STORE_AWARE_PATHS.includes(location.pathname);
  const showStoreSwitcher = (can("orders.view") || can("dashboard.admin")) && isStoreAwarePage;

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      <div className="hidden lg:block">{Sidebar}</div>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{Sidebar}</div>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 shrink-0 items-center gap-3 border-b border-slate-200 bg-white/95 px-4 sm:px-6 backdrop-blur-md">
          <button className="lg:hidden text-slate-600" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="h-6 w-6" />
          </button>

          {showStoreSwitcher && (
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline text-xs font-semibold uppercase tracking-wider text-slate-400">Toko</span>
              <Select value={activeStore} onValueChange={setActiveStore}>
                <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="store-switcher">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Toko</SelectItem>
                  {stores.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="ml-auto flex items-center gap-2">
            {can("inventory.view") && <StockAlerts />}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-100" data-testid="user-menu-btn">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white">
                    {initials}
                  </div>
                  <div className="hidden sm:block text-left leading-tight">
                    <p className="text-sm font-medium text-slate-800">{user?.name}</p>
                    <p className="text-[11px] text-slate-400">{user?.role_name}</p>
                  </div>
                  <ChevronsUpDown className="h-4 w-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-rose-600">
                  <LogOut className="mr-2 h-4 w-4" /> Keluar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8">{children}</div>
        </main>
      </div>
    </div>
  );
}
